import React, { useCallback, useEffect, useState } from 'react';
import {
  Container,
  AddPacienteButton,
  ContainerButton,
  BotaoLimpar,
  BotaoSalvar,
  BotoesContainer,
  InputObs,
  InputValorContato,
} from './styles';

import { returnAxiosError } from '../../utils/returnAxiosError';

import Dialog from '../../components/dialog';
import Paciente from '../Paciente';

import api from '../../services/api';
import { useToast } from '../../context/ToastContext';
import { useIndexedDB } from '../../context/IndexedDBContext';
import { useAuth } from '../../context/AuthContext';
import ComboboxComFiltro from '../../components/autocomplete';

import ICategoria from '../../routes/vms/ICategoria';
import IPaciente from '../../routes/vms/IPaciente';
import IListaEspera from '../../routes/vms/IListaEspera';

import { FiPlus, FiSave, FiRefreshCw } from 'react-icons/fi';
import IPacienteContatos from '../../routes/vms/IPacienteContatos';

interface ListaEspera {
  paciente_id: string;
  categoria_id: string;
  ordem: number;
  obs: string;
}

interface EsperaProps {
  onClose: () => void;
  esperaSelecionada: IListaEspera | undefined;
}

const Espera: React.FC<EsperaProps> = ({ onClose, esperaSelecionada }) => {
  const [categoria, setCategoria] = useState<string | null>(null);
  const [categoriaErro, setCategoriaErro] = useState<string>('');
  const [categorias, setCategorias] = useState<ICategoria[]>([]);

  const [paciente, setPaciente] = useState<string | null>(null);
  const [pacienteContato, setPacienteContato] = useState<string>('');
  const [pacienteErro, setPacienteErro] = useState<string>('');
  const [pacientes, setPacientes] = useState<IPaciente[]>([]);

  const [obs, setObs] = useState<string>('');

  const [isDialogOpen, setDialogOpen] = useState(false);

  const { addToast } = useToast();
  const { lerIndexedDB, excluirIndexedDB } = useIndexedDB();
  const { user } = useAuth();

  const handleOpenDialog = () => setDialogOpen(true);

  const carregaContatoPaciente = useCallback(async (paciente_id: string) => {
    const response = await api.get(`/pacientes/${paciente_id}`);

    const contatos: IPacienteContatos[] = response.data.contatos;
    const contato = contatos.find(
      contato => contato.tipo.includes('FONE') || contato.tipo.includes('CEL'),
    );

    setPacienteContato(contato ? contato.valor : '');
  }, []);

  const handleCloseDialog = useCallback(async () => {
    setDialogOpen(false);

    lerIndexedDB<string>(
      `${user.empresa.url_frontend}:id-paciente-gravado`,
    ).then(idIndexedDB => {
      if (idIndexedDB !== null) {
        setPaciente(String(idIndexedDB));
        carregaContatoPaciente(String(idIndexedDB));
        atualizaDados();

        excluirIndexedDB(`${user.empresa.url_frontend}:id-paciente-gravado`);
      }
    });
  }, [
    lerIndexedDB,
    excluirIndexedDB,
    user.empresa.url_frontend,
    carregaContatoPaciente,
  ]);

  const aoAlterarObs = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      setObs(event.target.value);
    },
    [],
  );

  const aoAlterarCategoria = (novoConvenio: string | null) => {
    setCategoria(novoConvenio);
    setCategoriaErro('');
  };

  const aoAlterarPaciente = (novoPaciente: string | null) => {
    setPaciente(novoPaciente);
    setPacienteErro('');
  };

  const validacoesEspera = useCallback(() => {
    setPacienteErro('');
    setCategoriaErro('');

    let erroValidacao = false;
    if (!categoria) {
      setCategoriaErro('Informação obrigatória!');
      erroValidacao = true;
    }
    if (!paciente) {
      setPacienteErro('Informação obrigatória!');
      erroValidacao = true;
    }

    if (erroValidacao) {
      addToast({
        type: 'info',
        title: 'Pendências na validação',
        description:
          'Efetue as correções indicadas em cada campo e tente novamente.',
      });
      return false;
    }
  }, [addToast, categoria, paciente]);

  const aoLimparCampos = useCallback(() => {
    setCategoria(null);
    setPaciente(null);
    setPacienteContato('');
    setObs('');
  }, []);

  const aoSalvar = useCallback(async () => {
    if (validacoesEspera() === false) {
      return;
    }

    const response = await api.get<number>('espera/ultimaOrdem');
    const ordem = response.data;

    const esperaGravar: ListaEspera = {
      categoria_id: categoria ? categoria : '',
      paciente_id: paciente ? paciente : '',
      ordem: ordem + 1,
      obs: obs,
    };

    try {
      if (!esperaSelecionada) {
        await api.post('espera', esperaGravar);
      } else {
        await api.put(`espera/${esperaSelecionada.id}`, {
          categoria_id: categoria ? categoria : '',
          paciente_id: paciente ? paciente : '',
          ordem: esperaSelecionada.ordem,
          obs: obs,
        });
      }
    } catch (error) {
      addToast(returnAxiosError(error));

      return;
    }
    aoLimparCampos();
    onClose();
  }, [
    addToast,
    aoLimparCampos,
    obs,
    categoria,
    paciente,
    validacoesEspera,
    onClose,
    esperaSelecionada,
  ]);

  const atualizaDados = async () => {
    try {
      const [categoriasResponse, pacientesResponse] = await Promise.all([
        api.get<ICategoria[]>('/categoria/lista'),
        api.get<IPaciente[]>('/pacientes/todos'),
      ]);

      setCategorias(categoriasResponse.data);
      setPacientes(pacientesResponse.data);
    } catch (error) {
      console.error('Erro ao buscar dados:', error);
    }
  };

  useEffect(() => {
    atualizaDados();
  }, []);

  useEffect(() => {
    if (esperaSelecionada) {
      setPaciente(esperaSelecionada.paciente.id);
      setCategoria(esperaSelecionada.categoria.id);
      setObs(esperaSelecionada.obs);

      carregaContatoPaciente(esperaSelecionada.paciente.id);
    }
  }, [esperaSelecionada, carregaContatoPaciente]);

  return (
    <Container>
      <span>
        <ContainerButton>
          <ComboboxComFiltro
            opcoes={pacientes}
            value={paciente}
            label="Paciente"
            onChange={aoAlterarPaciente}
            error={pacienteErro !== ''}
            helperText={pacienteErro}
            size={2}
          />

          <AddPacienteButton
            title="Cadastrar Paciente"
            onClick={handleOpenDialog}
          >
            <FiPlus />
          </AddPacienteButton>

          <Dialog isOpen={isDialogOpen} onClose={handleCloseDialog}>
            <Paciente
              externa={true}
              espera={true}
              onClose={handleCloseDialog}
            />
          </Dialog>
        </ContainerButton>

        <InputValorContato
          variant="outlined"
          margin="normal"
          id="valorContato"
          label=" Contato"
          name="valorContato"
          value={pacienteContato}
          disabled={true}
        />
      </span>

      <ComboboxComFiltro
        opcoes={categorias}
        value={categoria}
        label="Categoria"
        onChange={aoAlterarCategoria}
        error={categoriaErro !== ''}
        helperText={categoriaErro}
      />

      <InputObs
        variant="outlined"
        margin="normal"
        id="obs"
        label="Observações"
        name="obs"
        value={obs}
        onChange={aoAlterarObs}
      />

      <BotoesContainer>
        <BotaoSalvar onClick={aoSalvar}>
          <FiSave />
          Salvar
        </BotaoSalvar>

        <BotaoLimpar onClick={aoLimparCampos}>
          <FiRefreshCw />
          Limpar
        </BotaoLimpar>
      </BotoesContainer>
    </Container>
  );
};

export default Espera;
